import { AxiosError, isAxiosError } from 'axios'
import {
  DocumentStatus,
  DownloadPdfParams,
  IRecipient,
  InvoiceType,
  LinkReference,
  REDIRECTS_TYPES,
  RecipientRolesEnum,
  UpdateSignatureParams,
  counter,
  getTranslatedErrorMessage,
  setFillableFieldsDataIntoDocument,
} from '@gohighlevel/ghl-proposals-common'
import { computed, inject, ref } from 'vue'

import { DocumentServices } from '@/service/DocumentService'
import { getDrRegionQuerySuffix } from '@/util/region'
import { router } from '@/router'
import { useCertificate } from './useCertificate'
import { useDocumentStore } from '@/store/document'
import { useI18n } from 'vue-i18n'
import { useNotification } from '@gohighlevel/ghl-ui'
import { useRoute } from 'vue-router'

const redirectingToInvoice = ref(false)
const redirectingToRedirectUrl = ref(false)
const redirectUrl = ref('')
const validatingInvoice = ref(false)
const invoiceRedirectionDetails = ref({
  invoiceId: '',
  documentId: '',
})
const maxSecondsTillRedirect = ref(10)
const secondsTillRedirectToPayment = ref(maxSecondsTillRedirect.value)
const secondsTillRedirectToRedirectUrl = ref(maxSecondsTillRedirect.value)
const loading = ref(false)
const loadingDownloadPdf = ref(false)
const pageData = ref<any>({})

export const useDocument = () => {
  const route = useRoute()
  const isPreview = inject<boolean>('isPreview')
  const isMobilePreview = inject<boolean>('isMobilePreview', false)

  const store = useDocumentStore()
  const { t, locale } = useI18n()
  const {
    setDocumentData,
    setDocumentStatus,
    setUserTriggeredAcceptApiInCurrentFlow,
    setIsEditing,
    setRecipientLinkReference,
    setPaymentInfo,
  } = store

  const documentVersion = computed(() => (store.document ? store.document.version : 1))

  const isCurrantUserIsLastAcceptDoc = computed(() => {
    const n = store.recipients.length - store.recipients.filter(element => element.hasCompleted === true).length
    return n === 1 ? true : false
  })

  const isAllSigned = computed(() => {
    const signers = store.recipients.filter(({ role }) => role === RecipientRolesEnum.SIGNER)
    if (signers && signers.length > 0) {
      return signers.every(({ hasCompleted }) => hasCompleted) || false
    }
    return false
  })

  const isEditable = computed(() => store.isEditable)
  const __document = computed(() => store.document)
  // const signatureElementCount = computed(() => store.signatureElementCount)
  const recipient = computed(() => {
    return store.activeRecipient || ({} as IRecipient)
  })
  const recipientLinkReference = computed(() => store.recipientLinkReference)
  const notification = useNotification()
  const { getCertificateDetails } = useCertificate()

  const enableNotification = (type: 'success' | 'info' | 'warning' | 'error', title: string, description: string) => {
    notification[type]({
      title,
      description,
      duration: 10000,
    })
  }

  const getDocumentData = async (
    isPreview: boolean,
    isPDFView: boolean,
    isMobilePreview: boolean,
    AllowOldPreviewPageFeatureFlag = true
  ) => {
    try {
      loading.value = true
      setUserTriggeredAcceptApiInCurrentFlow(false)
      let requestParams = {}
      if (route.name === 'document-v1' || route.name === 'status-success') {
        requestParams = {
          referenceId: (route?.params?.referenceId || '') as string,
        }
      } else {
        const recipientId: string = (route?.params?.recipientId || '') as string
        requestParams = {
          id: route.params.documentId as string,
          isPreview,
          isPDFView,
          recipientId,
        }
      }

      if (isPreview || isMobilePreview) {
        if (AllowOldPreviewPageFeatureFlag) {
          const { data } = await DocumentServices.getDocument(requestParams)
          setDocument(data)
        } else {
          const { data } = await DocumentServices.getDocumentPreview(requestParams, isMobilePreview)
          setDocument(data)
        }
      } else {
        const { data } = await DocumentServices.getDocument(requestParams)
        setDocument(data)
      }

      if (isAllSigned.value && isPDFView) {
        await getCertificateDetails()
      }
    } catch (error) {
      console.error(error)
      if (isAxiosError(error)) {
        const axiosError = error as AxiosError
        const displayMessage = getTranslatedErrorMessage(axiosError, t)
        router.push({
          name: 'document-error',
          params: { type: '404' },
          query: { message: displayMessage },
        })
      } else {
        router.push({
          name: 'document-error',
          params: { type: '404' },
          query: { message: t('common.errors.generic') },
        })
      }
    } finally {
      loading.value = false
    }
  }

  const setDocument = (data: any) => {
    if (data) {
      const mappedPages = setFillableFieldsDataIntoDocument(
        data.document.pages,
        data.document.fillableFields || [],
        data.document.pricingTables
      )
      pageData.value = mappedPages
      setDocumentData({ ...data.document, pages: mappedPages })
      if (data?.recipientLinkReference) {
        setRecipientLinkReference(data.recipientLinkReference as LinkReference)
      }
      store.setInvoiceTypeAndIntervalType(data.document?.invoiceType, data.document?.intervalType)

      if (data?.paymentInfo) {
        setPaymentInfo(data.paymentInfo)
      }

      setIsEditing(isEditable.value && store.signatureElementCountForRecipient <= 0)
    }
  }

  const acceptSignature = async () => {
    try {
      loading.value = true
      const signedDateCCUser = recipient.value?.role === RecipientRolesEnum.USER ? new Date().toISOString() : ''
      const isFillableFieldsValid = store.validateFillableFields()
      if (!isFillableFieldsValid) {
        enableNotification('error', t('common.error'), t('documentViewer.fillableFieldsValidationMessage'))
        return
      }
      const fillableFields = store.getFillableFields()
      const requestParams = {
        ...(route.params.documentId && {
          documentId: route.params.documentId as string,
        }),
        ...(recipient.value?.id && { recipientId: recipient.value.id }),
        ...(recipient.value?.imgUrl && { signature: recipient.value?.imgUrl }),
        ...(recipient.value?.initialsImgUrl && {
          initials: recipient.value?.initialsImgUrl,
        }),
        signedDate: recipient.value?.signedDate || signedDateCCUser || new Date(),
        ...(recipientLinkReference.value?.referenceId && {
          referenceId: recipientLinkReference.value.referenceId,
        }),
        fillableFields,
        pricingTables: store.getPricingLineItems(),
        ...(store.userPosition && { userPosition: store.userPosition }),
      }
      const { data } = await DocumentServices.acceptDocument(requestParams as UpdateSignatureParams)
      if (data.success) {
        setDocumentStatus(DocumentStatus.ACCEPTED)
        setUserTriggeredAcceptApiInCurrentFlow(true)
        const message = isCurrantUserIsLastAcceptDoc.value
          ? t('documentViewer.documentSignedMessage')
          : t('documentViewer.documentAccepterMessage')

        enableNotification('success', t('common.success'), message)
        setDocument(data?.document)
        if (!data?.isRedirectToPaymentCompletion) {
          await redirectToRedirectUrl(data.redirectSettings)
        } else {
          if (data.invoiceType === InvoiceType.ONETIME) {
            const referenceId = (route.params.referenceId as string) || recipientLinkReference.value?.referenceId || data?.referenceId || ''
            await redirectToInvoice(data, referenceId)
          } else {
            await handleRecurringPayment(data)
          }
        }
      }
    } catch (error) {
      console.error(error)
    } finally {
      loading.value = false
    }
  }

  async function redirectToRedirectUrl(redirectSettings: {
    enableDocumentRedirectUrl: boolean
    documentRedirectUrl: string
    documentRedirectType: string
  }) {
    if (redirectSettings && redirectSettings?.enableDocumentRedirectUrl) {
      redirectingToRedirectUrl.value = true
      redirectUrl.value = redirectSettings.documentRedirectUrl || ''
      const openInNewTab = redirectSettings.documentRedirectType === REDIRECTS_TYPES.NEW_TAB
      counter({
        startCount: 5,
        timeGapInMs: 1000,
        onEnd: () => {
          if (redirectUrl.value) {
            const handlePopupBlock = () => {
              enableNotification('error', t('common.error'), t('documentViewer.popupBlocked'))
            }
            if (!openInNewTab) {
              redirectingToRedirectUrl.value = false
              window.location.href = redirectUrl.value
            }

            if (openInNewTab) {
              const newWindow = window.open(redirectUrl.value, openInNewTab ? '_blank' : '_self')
              if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                handlePopupBlock()
              } else {
                redirectingToRedirectUrl.value = false
              }
            }
          }
        },
        onTick: count => {
          secondsTillRedirectToRedirectUrl.value = count
        },
      })
    }
  }

  async function pollRecurringInvoiceForPayment(mainData: any) {
    const referenceId = (route?.params?.referenceId as string) || recipientLinkReference.value?.referenceId || mainData?.referenceId || ''

    if (!referenceId || referenceId.trim() === '') {
      console.error('No referenceId available for recurring invoice payment')
      return
    }

    const { data } = await DocumentServices.fetchRecurringInvoiceForPayment(referenceId)
    if (data.invoiceId) {
      validatingInvoice.value = false
      await redirectToInvoice(
        {
          ...mainData,
          ...data,
        },
        referenceId
      )
    } else {
      setTimeout(() => {
        pollRecurringInvoiceForPayment(mainData)
      }, 1000 * 10)
    }
  }
  async function handleRecurringPayment(data: any) {
    if (data.enableDirectPayment && data.isDueInNextXMinutes) {
      validatingInvoice.value = true
      pollRecurringInvoiceForPayment(data)
    }
  }

  async function redirectToInvoice(data: any, refId: string) {
    if (data.invoiceId && data.enableDirectPayment) {
      redirectingToInvoice.value = true
      invoiceRedirectionDetails.value = {
        invoiceId: data.invoiceId,
        documentId: data.documentId,
      }
      counter({
        startCount: 2,
        timeGapInMs: 1000,
        onEnd: () => {
          let currentHost = ''
          if (data?.redirectSettings && data?.redirectSettings?.enableDocumentRedirectUrl && data?.redirectSettings?.documentRedirectUrl) {
            currentHost = data?.redirectSettings?.documentRedirectUrl
          } else {
            if (refId && refId.trim() !== '') {
              currentHost =
                window.location.origin +
                '/documents/v1/status/' +
                refId +
                '?invoice=' +
                data.invoiceId +
                `&locale=${route.query.locale || locale.value}` +
                getDrRegionQuerySuffix()
            }
          }
          if (currentHost && currentHost.trim() !== '') {
            const redirectType = data?.redirectSettings?.documentRedirectType === REDIRECTS_TYPES.NEW_TAB ? '_blank' : '_self'
            const url = data.url + '?redirectType=' + redirectType + '&redirectUrl=' + encodeURIComponent(currentHost)
            window.location.replace(url)
          } else {
            window.location.replace(data.url)
          }
        },
        onTick: count => {
          secondsTillRedirectToPayment.value = count
        },
      })
    }
  }

  async function downloadPdf(isPreview = false, options?: DownloadPdfParams) {
    try {
      loading.value = true
      loadingDownloadPdf.value = true
      const { data } = await DocumentServices.downloadPdf(
        options
          ? options
          : {
              documentId: __document.value._id,
              altType: 'location',
              altId: __document.value.locationId,
              isPublicRequest: isPreview,
              ...(process.env.NODE_ENV !== 'production' && {
                baseUrlForPdf: window.location.origin,
              }),
              ...(process.env.NODE_ENV !== 'production' && {
                sourceEnvironment: process.env.NODE_ENV,
              }),
            }
      )
      return data
    } catch (err) {
      console.error(err)
      if (isAxiosError(err)) {
        const axiosError = err as AxiosError
        if (axiosError.response) {
          const displayMessage = getTranslatedErrorMessage(axiosError, t)
          enableNotification('error', t('common.error'), displayMessage)
        } else {
          enableNotification('error', t('common.error'), t('common.errors.generic'))
        }
      } else {
        enableNotification('error', t('common.error'), t('common.errors.generic'))
      }
    } finally {
      loading.value = false
      loadingDownloadPdf.value = false
    }
  }

  const downloadProposalPdf = async (options?: DownloadPdfParams) => {
    const data = await downloadPdf(!isPreview, options)
    if (data?.url) {
      const { url } = data
      // Check if running on iOS
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
      if (isIOS) {
        // For iOS, directly open the URL
        window.location.href = url
      } else {
        const anchor = document.createElement('a')
        anchor.setAttribute('href', url)
        anchor.setAttribute('download', `${store.document.name}.pdf`)
        const clickHandler = () => {
          setTimeout(() => {
            URL.revokeObjectURL(url)
            anchor.removeEventListener('click', clickHandler, false)
          }, 150)
        }
        anchor.addEventListener('click', clickHandler, false)
        anchor.click()
      }
    }
  }

  return {
    loading,
    loadingDownloadPdf,
    pageData,
    getDocumentData,
    // saveSignature,
    isPreview,
    isMobilePreview,
    acceptSignature,
    downloadPdf,
    redirectingToInvoice,
    redirectingToRedirectUrl,
    invoiceRedirectionDetails,
    secondsTillRedirectToPayment,
    secondsTillRedirectToRedirectUrl,
    maxSecondsTillRedirect,
    redirectUrl,
    downloadProposalPdf,
    validatingInvoice,
    documentVersion,
  }
}
